/** Optional y-axis hint for a plot series. */
export type AxisRangeHint = Readonly<{
  min: number;
  max: number;
  center: number;
}>;

/** Bar payload whose width is expressed in x-domain units. */
export type BarWidthValue = Readonly<{
  y: number;
  width: number;
}>;

/** Interval payload for range-band style renderers. */
export type RangeYValue = Readonly<{
  low: number;
  high: number;
}>;

/** OHLC payload for candlestick and OHLC renderers. */
export type OhlcValue = Readonly<{
  open: number;
  high: number;
  low: number;
  close: number;
}>;

/** Point payload for scatter and bubble-style renderers. */
export type PointValue = Readonly<{
  y: number;
  radius: Option<number>;
  color: Option<string>;
}>;

export type RecordFieldRole =
  | "y"
  | "radius"
  | "color"
  | "open"
  | "high"
  | "low"
  | "close"
  | "custom";

export type RecordFieldRoles = Readonly<Record<string, RecordFieldRole>>;

/** Escape-hatch payload for shapes not yet promoted to built-ins. */
export type RecordValue = Readonly<Record<string, UntypedJson>>;

export type PayloadShape =
  | Readonly<{ kind: "scalar" }>
  | Readonly<{ kind: "bar-width" }>
  | Readonly<{ kind: "range-y" }>
  | Readonly<{ kind: "ohlc" }>
  | Readonly<{ kind: "point" }>
  | Readonly<{ kind: "record"; fields: RecordFieldRoles }>;

export type PayloadShapeKind = PayloadShape["kind"];

/** Built-in payload union for the initial plot-data surface. */
export type PlotValue =
  | number
  | BarWidthValue
  | RangeYValue
  | OhlcValue
  | PointValue
  | RecordValue;

export namespace PayloadShape {
  export const SCALAR: Extract<PayloadShape, { kind: "scalar" }> = Object.freeze({
    kind: "scalar",
  });

  export const BAR_WIDTH: Extract<PayloadShape, { kind: "bar-width" }> = Object.freeze({
    kind: "bar-width",
  });

  export const RANGE_Y: Extract<PayloadShape, { kind: "range-y" }> = Object.freeze({
    kind: "range-y",
  });

  export const OHLC: Extract<PayloadShape, { kind: "ohlc" }> = Object.freeze({
    kind: "ohlc",
  });

  export const POINT: Extract<PayloadShape, { kind: "point" }> = Object.freeze({
    kind: "point",
  });

  export function record(
    fields: RecordFieldRoles,
  ): Extract<PayloadShape, { kind: "record" }> {
    return Object.freeze({
      kind: "record",
      fields: Object.freeze({ ...fields }),
    });
  }
}
